import { createFileRoute } from "@tanstack/react-router";

import { SectionHeading } from "../components/SectionHeading";
import { CtaBand } from "../components/CtaBand";
import { procesStappen } from "../lib/data";
import { SITE_URL, ogImageMeta, ogImageOver } from "../lib/seo";

const overImageUrl =
  "/__l5e/assets-v1/6a0c4d30-c93d-4621-b7ba-fc54aeebbd01/over-fedor-kozijnen.webp";
const steigerImageUrl =
  "/__l5e/assets-v1/34e96858-41ee-4b36-a16e-8b350712f954/over-steiger-verbouwing.jpg";
const steigerImage768Url =
  "/__l5e/assets-v1/31cbeae2-a794-4c71-9417-bdc40e267be0/over-steiger-verbouwing-768.webp";
const kraanImageUrl =
  "/__l5e/assets-v1/5fc77690-c5f3-402b-a820-4519963b8f26/over-renovatie-kraan.jpg";
const kraanImage768Url =
  "/__l5e/assets-v1/66cc8e6c-e433-4fd4-ab9f-11669f23ec29/over-renovatie-kraan-768.webp";

export const Route = createFileRoute("/over")({
  head: () => ({
    meta: [
      { title: "Over Fedor Timmerwerken — Timmerbedrijf Dordrecht" },
      {
        name: "description",
        content:
          "Fedor Timmerwerken is een veelzijdig timmer- en aannemersbedrijf uit Dordrecht, opgericht in 2018 door Fedor de Vries. Alles ter plekke op maat gemaakt.",
      },
      {
        property: "og:title",
        content: "Over Fedor Timmerwerken — Timmerbedrijf Dordrecht",
      },
      {
        property: "og:description",
        content:
          "Veelzijdig timmer- en aannemersbedrijf uit Dordrecht, opgericht in 2018 door Fedor de Vries.",
      },
      { property: "og:url", content: `${SITE_URL}/over` },
      ...ogImageMeta(ogImageOver, "Fedor Timmerwerken aan het werk — kozijn repareren en plaatsen"),
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}/over` },
      {
        rel: "preload",
        as: "image",
        href: overImageUrl,
        fetchPriority: "high",
      },
    ],
  }),
  component: OverPage,
});

function OverPage() {
  return (
    <>
      <div className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading
            as="h1"
            eyebrow="Over ons"
            title="Over Fedor Timmerwerken"
            description="Fedor Timmerwerken is een veelzijdig eenmansbedrijf, opgericht in 2018 door Fedor de Vries — inmiddels uitgegroeid tot een aannemersbedrijf."
            align="center"
            className="mx-auto"
          />

          <div className="mt-10 sm:mt-14 grid gap-10 lg:grid-cols-2 lg:items-center">
            <div className="order-2 lg:order-1">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
                Zolang het van hout gemaakt is
              </h2>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Met een grondslag in timmerwerk biedt Fedor Timmerwerken een indrukwekkende
                diversiteit aan timmerdiensten. Zolang het van hout gemaakt is, is Fedor
                Timmerwerken de juiste keuze voor je project. Laat je dus niet ontmoedigen als wat
                je in gedachten had er niet tussen staat.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Ook als je het nog niet zeker weet, is het een goede keuze om contact op te nemen.
                We komen graag bij je thuis langs om te luisteren, te kijken en mee te denken. We
                vinden het uitermate belangrijk dat je compleet gelukkig bent met ons werk wanneer
                we vertrekken.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Doordat producten zoals inbouwkasten, dakkapellen, schuren en vlonders altijd
                ter plekke op maat worden gemaakt, sluit het resultaat gegarandeerd aan op je
                wensen. We hechten veel waarde aan innovatie, ontwikkeling en kwaliteit en werken
                samen met vakmensen uit verschillende disciplines om altijd de hoogste kwaliteit te
                bieden.
              </p>
            </div>

            <div className="order-1 lg:order-2">
              <div className="aspect-[2/1] overflow-hidden rounded-2xl border border-border/60">
                <img
                  src={overImageUrl}
                  alt="Kozijn repareren en een nieuw kozijn plaatsen — Fedor Timmerwerken aan het werk"
                  width={1600}
                  height={800}
                  fetchPriority="high"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>

          <div className="mt-24">
            <SectionHeading
              eyebrow="Fedor in beeld"
              title="Op de bouwplaats"
              description="Een kijkje op de bouwplaats — van steigers en spandoek tot kraanwerk bij een renovatie."
              align="center"
              className="mx-auto"
            />

            <div className="mt-10 sm:mt-14 grid gap-6 sm:grid-cols-2">
              <figure>
                <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-border/60">
                  <img
                    src={steigerImageUrl}
                    srcSet={`${steigerImage768Url} 768w, ${steigerImageUrl} 1920w`}
                    sizes="(min-width: 640px) 50vw, calc(100vw - 2rem)"
                    alt="Steiger met Fedor Timmerwerken-spandoek tijdens een verbouwing in Dordrecht"
                    width={1920}
                    height={1440}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </div>
                <figcaption className="mt-3 text-center text-sm text-muted-foreground">
                  Verbouwing in volle gang — de steigers staan, het spandoek hangt.
                </figcaption>
              </figure>

              <figure>
                <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-border/60">
                  <img
                    src={kraanImageUrl}
                    srcSet={`${kraanImage768Url} 768w, ${kraanImageUrl} 1920w`}
                    sizes="(min-width: 640px) 50vw, calc(100vw - 2rem)"
                    alt="Bus van Fedor Timmerwerken terwijl een kraan kozijnen naar een renovatieproject hijst"
                    width={1920}
                    height={1440}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </div>
                <figcaption className="mt-3 text-center text-sm text-muted-foreground">
                  Nieuwe kozijnen worden met de kraan naar boven gehesen.
                </figcaption>
              </figure>
            </div>
          </div>

          <div className="mt-24">
            <SectionHeading
              eyebrow="Ons proces"
              title="Van idee naar realiteit in vier stappen"
              description="Om van idee naar realiteit te komen werkt Fedor Timmerwerken samen met jou. Dit proces is op te delen in vier globale stappen."
              align="center"
              className="mx-auto"
            />

            <ol className="mt-10 sm:mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {procesStappen.map((stap) => (
                <li
                  key={stap.nummer}
                  className="relative rounded-xl border border-border/60 bg-card p-6"
                >
                  <span
                    className="font-heading text-5xl font-extrabold text-primary/20"
                    aria-hidden="true"
                  >
                    {stap.nummer}
                  </span>
                  <h3 className="mt-3 font-heading text-lg font-semibold text-foreground">
                    {stap.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {stap.description}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-24 rounded-2xl border border-border/60 bg-card p-8 md:p-10">
            <div className="flex flex-col items-start gap-6 md:flex-row md:items-center md:justify-between">
              <div>
                <span className="font-heading text-sm font-semibold uppercase tracking-wider text-primary">
                  In de media
                </span>
                <h3 className="mt-2 font-heading text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                  Zin om verder te lezen over Fedor Timmerwerken?
                </h3>
                <p className="mt-2 text-base leading-relaxed text-muted-foreground">
                  Onze suggestie is deze blogpost van Klium!
                </p>
              </div>
              <a
                href="https://www.klium.nl/nl/blog/fedor-timmerwerken/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex shrink-0 items-center justify-center rounded-full bg-primary px-6 py-3 font-heading text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Lees het artikel
              </a>
            </div>
          </div>
        </div>
      </div>

      <CtaBand />
    </>
  );
}
